// utils/spanishSpellChecker.js
// Utilidad ligera de control y corrección ortográfica en español para descripciones de líneas

const REGLAS_DIRECTAS = {
  // Acentuación común en -ción / -sión y términos comerciales / técnicos
  'revision': 'revisión',
  'instalacion': 'instalación',
  'reparacion': 'reparación',
  'sustitucion': 'sustitución',
  'inspeccion': 'inspección',
  'alineacion': 'alineación',
  'facturacion': 'facturación',
  'construccion': 'construcción',
  'atencion': 'atención',
  'ubicacion': 'ubicación',
  'proteccion': 'protección',
  'demolicion': 'demolición',
  'renovacion': 'renovación',
  'verificacion': 'verificación',
  'calibracion': 'calibración',
  'certificacion': 'certificación',
  'desinfeccion': 'desinfección',
  'extraccion': 'extracción',
  'modificacion': 'modificación',
  'rectificacion': 'rectificación',
  'supervision': 'supervisión',
  'transmision': 'transmisión',
  'emision': 'emisión',
  'pension': 'pensión',
  'presion': 'presión',
  'aplicacion': 'aplicación',
  'cancelacion': 'cancelación',
  'organizacion': 'organización',
  'pavimentacion': 'pavimentación',
  'cimentacion': 'cimentación',
  'impermeabilizacion': 'impermeabilización',
  'aislacion': 'aislación',

  // Automoción, herramientas y materiales
  'bateria': 'batería',
  'baterias': 'baterías',
  'neumatico': 'neumático',
  'neumaticos': 'neumáticos',
  'vehiculo': 'vehículo',
  'vehiculos': 'vehículos',
  'diagnostico': 'diagnóstico',
  'diagnosticos': 'diagnósticos',
  'mecanico': 'mecánico',
  'mecanicos': 'mecánicos',
  'mecanica': 'mecánica',
  'mecanicas': 'mecánicas',
  'tecnico': 'técnico',
  'tecnicos': 'técnicos',
  'tecnica': 'técnica',
  'tecnicas': 'técnicas',
  'electrico': 'eléctrico',
  'electricos': 'eléctricos',
  'electrica': 'eléctrica',
  'electricas': 'eléctricas',
  'electronico': 'electrónico',
  'electronicos': 'electrónicos',
  'electronica': 'electrónica',
  'electronicas': 'electrónicas',
  'hidraulico': 'hidráulico',
  'hidraulicos': 'hidráulicos',
  'hidraulica': 'hidráulica',
  'hidraulicas': 'hidráulicas',
  'articulo': 'artículo',
  'articulos': 'artículos',
  'numero': 'número',
  'numeros': 'números',
  'ultimo': 'último',
  'ultimos': 'últimos',
  'periodico': 'periódico',
  'periodicos': 'periódicos',
  'garantia': 'garantía',
  'garantias': 'garantías',
  'poliza': 'póliza',
  'polizas': 'pólizas',
  'kilometros': 'kilómetros',
  'deposito': 'depósito',
  'depositos': 'depósitos',
  'valvula': 'válvula',
  'valvulas': 'válvulas',
  'camara': 'cámara',
  'camaras': 'cámaras',
  'liquido': 'líquido',
  'liquidos': 'líquidos',
  'optica': 'óptica',
  'opticas': 'ópticas',
  'lampara': 'lámpara',
  'lamparas': 'lámparas',
  'modulo': 'módulo',
  'modulos': 'módulos',
  'codigo': 'código',
  'codigos': 'códigos',
  'tuberia': 'tubería',
  'tuberias': 'tuberías',
  'fontaneria': 'fontanería',
  'albanil': 'albañil',
  'albanileria': 'albañilería',
  'carpinteria': 'carpintería',
  'cerrajeria': 'cerrajería',
  'cristaleria': 'cristalería',
  'tapiceria': 'tapicería',
  'carroceria': 'carrocería',
  'chaperia': 'chapería',
  'auditoria': 'auditoría',
  'auditorias': 'auditorías',
  'consultoria': 'consultoría',
  'consultorias': 'consultorías',
  'asesoria': 'asesoría',
  'asesorias': 'asesorías',
  'gestoria': 'gestoría',
  'gestorias': 'gestorías',
  'ingenieria': 'ingeniería',
  'envio': 'envío',
  'envios': 'envíos',
  'guia': 'guía',
  'guias': 'guías',
  'automatico': 'automático',
  'automaticos': 'automáticos',
  'automatica': 'automática',
  'automaticas': 'automáticas',
  'plastico': 'plástico',
  'plasticos': 'plásticos',
  'acustico': 'acústico',
  'acusticos': 'acústicos',
  'acustica': 'acústica',
  'acusticas': 'acústicas',
  'termico': 'térmico',
  'termicos': 'térmicos',
  'termica': 'térmica',
  'termicas': 'térmicas',
  'metalico': 'metálico',
  'metalicos': 'metálicos',
  'metalica': 'metálica',
  'metalicas': 'metálicas',
  'sintetico': 'sintético',
  'sinteticos': 'sintéticos',
  'sintetica': 'sintética',
  'sinteticas': 'sintéticas',
  'camion': 'camión',
  'distribucion': 'distribución',
  'climatizacion': 'climatización',
  'canbio': 'cambio',
  'linpieza': 'limpieza',
};

/**
 * Analiza un texto y devuelve las faltas ortográficas detectadas y sugerencias de corrección en español.
 * @param {string} texto
 * @returns {{ faltas: Array<{ palabra: string, sugerencia: string }>, textoSugerido: string }}
 */
export function comprobarOrtografiaTexto(texto = '') {
  if (!texto || typeof texto !== 'string' || !texto.trim()) {
    return { faltas: [], textoSugerido: '' };
  }

  const faltas = [];
  const palabrasMap = new Map();
  const tokens = texto.match(/\b[a-zA-zA-ZáéíóúñÁÉÍÓÚÑ]+\b/g) || [];

  for (const token of tokens) {
    const min = token.toLowerCase();

    // 1. Coincidencia directa en diccionario de reglas
    if (REGLAS_DIRECTAS[min] && REGLAS_DIRECTAS[min] !== min) {
      const sugerenciaBase = REGLAS_DIRECTAS[min];
      const sugerenciaFinal = token[0] === token[0].toUpperCase() && token[0] !== token[0].toLowerCase()
        ? sugerenciaBase.charAt(0).toUpperCase() + sugerenciaBase.slice(1)
        : sugerenciaBase;

      if (!palabrasMap.has(token)) {
        palabrasMap.set(token, sugerenciaFinal);
        faltas.push({ palabra: token, sugerencia: sugerenciaFinal });
      }
      continue;
    }

    // 2. Regla agudas en -cion / -sion sin tilde
    if ((min.endsWith('cion') || min.endsWith('sion')) && !min.endsWith('ción') && !min.endsWith('sión')) {
      const sugerenciaBase = min.slice(0, -4) + (min.endsWith('cion') ? 'ción' : 'sión');
      const sugerenciaFinal = token[0] === token[0].toUpperCase() && token[0] !== token[0].toLowerCase()
        ? sugerenciaBase.charAt(0).toUpperCase() + sugerenciaBase.slice(1)
        : sugerenciaBase;

      if (!palabrasMap.has(token)) {
        palabrasMap.set(token, sugerenciaFinal);
        faltas.push({ palabra: token, sugerencia: sugerenciaFinal });
      }
      continue;
    }

    // 3. Regla de sustantivos de oficio/actividad en -eria sin tilde (fontaneria -> fontanería)
    if (min.length > 5 && min.endsWith('eria') && !min.endsWith('ería')) {
      const sugerenciaBase = min.slice(0, -4) + 'ería';
      const sugerenciaFinal = token[0] === token[0].toUpperCase() && token[0] !== token[0].toLowerCase()
        ? sugerenciaBase.charAt(0).toUpperCase() + sugerenciaBase.slice(1)
        : sugerenciaBase;

      if (!palabrasMap.has(token)) {
        palabrasMap.set(token, sugerenciaFinal);
        faltas.push({ palabra: token, sugerencia: sugerenciaFinal });
      }
      continue;
    }
  }

  let textoSugerido = texto;
  if (faltas.length > 0) {
    for (const falta of faltas) {
      const regex = new RegExp(`\\b${falta.palabra}\\b`, 'g');
      textoSugerido = textoSugerido.replace(regex, falta.sugerencia);
    }
  }

  return {
    faltas,
    textoSugerido: textoSugerido !== texto ? textoSugerido : '',
  };
}
